import React, { useState, useEffect } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { businessApi, errorMessage } from '../../lib/api';
import ConfirmDialog from './ConfirmDialog';

const ClaimsTable = ({ title, status, type, businessId, icon: Icon, iconColor, onRedeemSuccess, hideHeader = false }) => {
  // Support both 'status' and 'type' props for flexibility
  const claimStatus = status || type || 'claimed';
  
  // If no icon provided, show content directly (used when wrapped in external container)
  const showHeader = !hideHeader && Icon;
  
  const [isExpanded, setIsExpanded] = useState(!showHeader); // Auto-expand if no header
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalClaims, setTotalClaims] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [redeeming, setRedeeming] = useState(null);
  const [claimToRedeem, setClaimToRedeem] = useState(null);

  // Fetch count on mount to show in collapsed view
  useEffect(() => {
    fetchClaimsCount();
    // Auto-fetch claims if no header (already expanded)
    if (!showHeader) {
      fetchClaims();
    }
  }, [claimStatus]);

  const fetchClaimsCount = async () => {
    try {
      const { data } = await businessApi.get('/business/claims', {
        params: { status: claimStatus, per_page: 1 },
      });
      setTotalClaims(data.total || 0);
    } catch (error) {
      console.error('Error fetching claims count:', error);
    }
  };

  const fetchClaims = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const { data } = await businessApi.get('/business/claims', {
        params: { status: claimStatus, per_page: 100 },
      });
      setClaims(data.claims || []);
      setTotalClaims(data.total || 0);
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = async () => {
    if (!isExpanded) {
      await fetchClaims();
    }
    setIsExpanded(!isExpanded);
  };

  const handleRedeem = async () => {
    const claim = claimToRedeem;
    if (!claim) return;

    setRedeeming(claim.id);
    try {
      await businessApi.post(`/business/claims/${claim.id}/redeem`, {});
      setClaimToRedeem(null);

      // Refresh the claims list and the count shown in the collapsed view
      await fetchClaims();
      await fetchClaimsCount();

      // Notify parent to refresh stats and other components
      if (onRedeemSuccess) {
        onRedeemSuccess();
      }
      toast.success('Deal marked as redeemed.');
    } catch (error) {
      console.error('Error redeeming claim:', error);
      toast.error(errorMessage(error, 'Failed to redeem this claim. Please try again.'));
    } finally {
      setRedeeming(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const calculateTimeBetween = (claimedAt, redeemedAt) => {
    if (!claimedAt || !redeemedAt) return '';
    
    const claimed = new Date(claimedAt);
    const redeemed = new Date(redeemedAt);
    const diffMs = redeemed - claimed;
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `(${hours}h ${minutes}m)`;
    }
    return `(${minutes}m)`;
  };

  const filteredClaims = claims.filter(claim => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      claim.user.masked_name.toLowerCase().includes(search) ||
      claim.user.masked_email.toLowerCase().includes(search) ||
      claim.redemption_code.toLowerCase().includes(search) ||
      claim.deal_title.toLowerCase().includes(search)
    );
  });

  return (
    <Card className={`border-0 ${showHeader ? 'shadow-lg' : 'shadow-none'} overflow-hidden`}>
      <CardContent className="p-0">
        {/* Header - Only shown when Icon is provided */}
        {showHeader && (
          <div
            className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={handleExpand}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Icon size={24} style={{ color: iconColor }} />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                  <p className="text-sm text-gray-600">
                    {totalClaims > 0 ? `${totalClaims} total` : 'No claims yet'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {loading && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />}
                {isExpanded ? (
                  <ChevronUp size={24} className="text-gray-400" />
                ) : (
                  <ChevronDown size={24} className="text-gray-400" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <div className={showHeader ? "border-t border-gray-200" : ""}>
            {/* Search Bar */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  aria-label="Search claims"
                  placeholder="Search by name, email, code, or deal..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading claims...</p>
              </div>
            )}

            {/* Table */}
            {!loading && (
            <div className="overflow-x-auto">
              {filteredClaims.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm ? 'No claims match your search' : `No ${claimStatus} claims yet`}
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {claimStatus === 'redeemed' ? 'Claimed' : 'Date'}
                      </th>
                      {claimStatus === 'redeemed' && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Redeemed
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      {claimStatus === 'claimed' && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClaims.map((claim) => (
                      <tr key={claim.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {claim.user.masked_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {claim.user.masked_email}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{claim.deal_title}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">
                            {formatDate(claim.claimed_at)}
                          </div>
                        </td>
                        {claimStatus === 'redeemed' && (
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">
                              {formatDate(claim.redeemed_at)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {calculateTimeBetween(claim.claimed_at, claim.redeemed_at)}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{claim.location_name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-mono font-semibold text-purple-600">
                            {claim.redemption_code}
                          </div>
                        </td>
                        {claimStatus === 'claimed' && (
                          <td className="px-4 py-4">
                            <Button
                              size="sm"
                              onClick={() => setClaimToRedeem(claim)}
                              disabled={redeeming === claim.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {redeeming === claim.id ? (
                                <span className="flex items-center gap-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                  Redeeming...
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 size={14} />
                                  Redeem
                                </span>
                              )}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            )}

            {/* Footer with count */}
            {!loading && filteredClaims.length > 0 && (
              <div className="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
                Showing {filteredClaims.length} of {totalClaims} {claimStatus} claims
              </div>
            )}
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={!!claimToRedeem}
        title="Mark this claim as redeemed?"
        message={claimToRedeem ? `Code ${claimToRedeem.redemption_code} for "${claimToRedeem.deal_title}". This cannot be undone.` : ''}
        confirmLabel="Mark redeemed"
        tone="primary"
        busy={!!redeeming}
        onConfirm={handleRedeem}
        onCancel={() => setClaimToRedeem(null)}
      />
    </Card>
  );
};

export default ClaimsTable;
